// app/javascript/lib/auto_save/form_serializer.js

const AUTHENTICITY_TOKEN = "authenticity_token";

/**
 * @typedef {string | string[]} SerializedValue
 */

/**
 * @param {HTMLFormElement} form
 * @returns {Record<string, SerializedValue>}
 */
export function serialize(form) {
  const formData = new FormData(form);

  return Array.from(formData.entries()).reduce((result, [key, value]) => {
    const v = String(value);

    if (result[key] !== undefined) {
      result[key] = [].concat(result[key], v);
    } else {
      result[key] = v;
    }

    return result;
  }, {});
}

/**
 * @param {HTMLFormElement} form
 * @param {Record<string, SerializedValue>} data
 */
export function restore(form, data) {
  Object.entries(data).forEach(([name, value]) => {
    if (name === AUTHENTICITY_TOKEN) return;
    restoreFormField(form, name, value);
  });
}

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 * @param {SerializedValue} value
 */
function restoreFormField(form, name, value) {
  const node = form.elements.namedItem(name);
  if (!node) return;

  // Group: radio/checkbox/repeated fields
  if (node instanceof RadioNodeList) {
    const inputs = Array.from(node).filter((el) => el instanceof HTMLInputElement);
    if (inputs.length === 0) return;

    switch (inputs[0].type) {
      case "radio":
        restoreRadioGroup(inputs, value);
        break;
      case "checkbox":
        restoreCheckboxGroup(inputs, value);
        break;
    }
    return;
  }

  if (node instanceof HTMLSelectElement && node.multiple) {
    restoreMultiSelect(node, value);
    return;
  }

  restoreSingleInput(node, value);
}

/**
 * @param {HTMLInputElement[]} inputs
 * @param {string} value
 */
function restoreRadioGroup(inputs, value) {
  inputs.forEach((inp) => {
    inp.checked = String(inp.value) === String(value);
  });
  inputs.find((inp) => inp.checked)?.dispatchEvent(new Event("change", { bubbles: true }));
}

/**
 * @param {HTMLInputElement[]} inputs
 * @param {string[]} values
 */
function restoreCheckboxGroup(inputs, values) {
  const set = new Set(values);
  inputs.forEach((inp) => {
    inp.checked = set.has(String(inp.value));
  });
  inputs[0]?.dispatchEvent(new Event("change", { bubbles: true }));
}

/**
 * @param {HTMLSelectElement} select
 * @param {string[]} values
 */
function restoreMultiSelect(select, values) {
  const set = new Set(values);
  Array.from(select.options).forEach((opt) => {
    opt.selected = set.has(String(opt.value));
  });
  select.dispatchEvent(new Event("change", { bubbles: true }));
}

/**
 * @param {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement} input
 * @param {string} value
 */
function restoreSingleInput(input, value) {
  if (input instanceof HTMLInputElement && input.type === "checkbox") {
    input.checked = value !== "" && value !== "0" && value !== "false";
  } else {
    input.value = value;
  }

  input.dispatchEvent(new Event("change", { bubbles: true }));
}
